// PRPathways.js - New Zealand
import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, MapPin, Users, ChevronDown, ChevronUp } from 'lucide-react';

const PRPathways = () => {
  const [expandedPathway, setExpandedPathway] = useState(null);

  const pathways = [
    {
      icon: Zap,
      title: 'Skilled Migrant Category (SMC)',
      subtitle: 'Points-based residence visa for skilled workers',
      description: 'The primary pathway for skilled workers to gain New Zealand residence. Points awarded for age, qualifications, work experience, and job offers.',
      details: {
        overview: 'The Skilled Migrant Category is New Zealand\'s main residence pathway for skilled workers. Applications are selected from the Expression of Interest (EOI) pool based on points scores.',
        programs: [
          {
            name: 'Skilled Migrant Category Resident Visa',
            requirements: 'Minimum 160 points to submit EOI (competitive selection at 180+ points). Points for age (20-39 = max points), qualifications (Level 7+ = 40-70 points), skilled employment (50-60 points), and NZ experience. Must have skilled job or job offer in NZ. English requirement: IELTS 6.5 overall (or equivalent).'
          },
          {
            name: 'Points Breakdown',
            requirements: 'Age 20-39: 30 points. Bachelor degree: 40 points, Master: 50 points, PhD: 70 points. Skilled employment in NZ: 50 points. Partner qualifications/employment: up to 20 points. NZ work experience (1+ years): 10 points. Qualifications in areas of absolute skills shortage: 10 points.'
          },
          {
            name: 'Job Offer Requirements',
            requirements: 'Must be in skilled occupation (ANZSCO skill levels 1-3). Genuine full-time position (30+ hours/week). Market rate salary. Employer must be accredited or demonstrate genuine need. Job offer significantly increases selection chances.'
          }
        ],
        keyPoints: [
          'Minimum 160 points to submit EOI, competitive selection at 180+',
          'EOI selections occur regularly (typically every 2 weeks)',
          'Job offer in New Zealand highly recommended',
          'English requirement: IELTS 6.5 overall minimum',
          'Processing time: 6-12 months after invitation',
          'Can include partner and dependent children'
        ]
      }
    },
    {
      icon: MapPin,
      title: 'Regional Pathways',
      subtitle: 'Work to Residence and regional visa options',
      description: 'Pathways through regional work experience or specific regional employer sponsorship programs.',
      details: {
        overview: 'Regional pathways provide alternative routes to residence for those willing to live and work outside Auckland, often with lower points requirements.',
        programs: [
          {
            name: 'Work to Residence (Accredited Employer)',
            requirements: 'Job offer from accredited employer in skilled occupation. Work visa granted for 30 months, then eligible to apply for residence. No points test required. Must meet health, character, and English requirements. Salary must meet median wage threshold.'
          },
          {
            name: 'Straight to Residence (Accredited Employer)',
            requirements: 'For highly skilled roles (typically ANZSCO level 1-2) with salary at or above NZD $106,080. Job offer from accredited employer. Can apply directly for residence without work visa stage. Faster pathway for high earners.'
          },
          {
            name: 'Regional Skilled Migration',
            requirements: 'Lower points requirements for those with job offers in regions outside Auckland. Additional points for regional employment. Commitment to work in region for specified period. Helps address regional skill shortages.'
          }
        ],
        keyPoints: [
          'No points test for Work to Residence pathway',
          'Employer must be Immigration NZ accredited',
          'Salary thresholds apply (median wage or higher)',
          'Regional employment provides additional points in SMC',
          'Straight to Residence for high earners (NZD $106,080+)',
          'Processing time varies by pathway (6-18 months)'
        ]
      }
    },
    {
      icon: Users,
      title: 'Family & Partner Pathways',
      subtitle: 'Partnership, parent, and dependent child categories',
      description: 'Residence pathways for partners, parents, and dependent children of New Zealand citizens or residents.',
      details: {
        overview: 'Family category visas allow New Zealand citizens and residents to sponsor eligible family members for residence.',
        programs: [
          {
            name: 'Partnership Resident Visa',
            requirements: 'Partner of NZ citizen or resident. Must be living together in genuine and stable relationship (12+ months). Can apply onshore or offshore. Two-stage process: temporary (2 years) then permanent. Sponsor must meet income requirements (NZD $43,322.76 for couple).'
          },
          {
            name: 'Parent Resident Visa',
            requirements: 'Parent of NZ citizen or resident who is adult and settled. Must meet balance of family test (equal or more children in NZ than elsewhere). Sponsor must meet income threshold (NZD $65,000+ for one parent, NZD $90,000+ for two). Health and character requirements apply.'
          },
          {
            name: 'Dependent Child Resident Visa',
            requirements: 'Child under 24 years old. Parent must be NZ citizen or resident. Single and financially dependent on parent. Can include adopted children and step-children. Must meet health and character requirements.'
          }
        ],
        keyPoints: [
          'Partnership visa requires genuine relationship evidence',
          'Parent visa has income requirements for sponsor',
          'Balance of family test applies to parent applications',
          'Processing time: 12-24 months for partnership, 24-36 months for parents',
          'Medical examinations required for all applicants',
          'Police certificates required from all countries lived in 12+ months'
        ]
      }
    }
  ];

  const togglePathway = (index) => {
    setExpandedPathway(expandedPathway === index ? null : index);
  };

  return (
    <section className="py-16 bg-[#F5F8FA]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Available PR Pathways
          </h2>
          <p className="text-gray-600 text-base font-light max-w-3xl mx-auto">
            Choose the pathway that best matches your profile, experience, and immigration goals.
          </p>
        </motion.div>

        {/* Pathways */}
        <div className="space-y-6">
          {pathways.map((pathway, index) => {
            const Icon = pathway.icon;
            const isExpanded = expandedPathway === index;

            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white rounded-lg shadow-sm overflow-hidden"
              >
                {/* Pathway Header */}
                <button
                  onClick={() => togglePathway(index)}
                  className="w-full p-6 flex items-start gap-4 hover:bg-gray-50 transition-colors text-left"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-[#202356] to-[#2542D4] rounded-lg flex items-center justify-center flex-shrink-0">
                    <Icon size={24} className="text-white" />
                  </div>
                  
                  <div className="flex-1">
                    <h3 className="text-xl font-semibold text-[#202356] mb-2">
                      {pathway.title}
                    </h3>
                    <p className="text-sm text-gray-500 font-light mb-2">
                      {pathway.subtitle}
                    </p>
                    <p className="text-gray-600 text-sm font-light">
                      {pathway.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    {isExpanded ? (
                      <ChevronUp size={24} className="text-[#2542D4]" />
                    ) : (
                      <ChevronDown size={24} className="text-[#2542D4]" />
                    )}
                  </div>
                </button>

                {/* Expanded Details */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 border-t border-gray-200">
                        <div className="pt-6 space-y-6">
                          {/* Overview */}
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-2">
                              Overview
                            </h4>
                            <p className="text-gray-600 text-sm font-light leading-relaxed">
                              {pathway.details.overview}
                            </p>
                          </div>

                          {/* Programs/Categories */}
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-3">
                              Programs & Requirements
                            </h4>
                            <div className="space-y-4">
                              {pathway.details.programs.map((program, pIndex) => (
                                <div key={pIndex} className="bg-[#F5F8FA] p-4 rounded-lg">
                                  <h5 className="text-sm font-semibold text-[#202356] mb-2">
                                    {program.name}
                                  </h5>
                                  <p className="text-gray-600 text-sm font-light leading-relaxed">
                                    {program.requirements}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Key Points */}
                          <div>
                            <h4 className="text-base font-semibold text-[#202356] mb-3">
                              Key Points
                            </h4>
                            <ul className="space-y-2">
                              {pathway.details.keyPoints.map((point, kIndex) => (
                                <li key={kIndex} className="flex items-start gap-2">
                                  <span className="w-1.5 h-1.5 bg-[#2542D4] rounded-full mt-2 flex-shrink-0"></span>
                                  <span className="text-gray-600 text-sm font-light">
                                    {point}
                                  </span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default PRPathways;
